import React, { useState } from 'react';
import { Currency, formatMoney } from '../../common/format';
import { RequestResponseViewer } from '../../common/request-response-viewer';
import {
  ChartSeriesConfig,
  CurrencyHeader,
  EvolutionChart,
  KpiCard,
  KpiGrid,
  ProjectionColumn,
  ProjectionTable,
  SimulationWarnings,
  buildEvolutionSeries,
  buildMonthlyProjectionColumns,
  buildProjectionColumns,
} from '../_shared';
import { APVSelectMonthlyRow, APVSelectPolicyRow, APVSelectResponse } from './service';

const CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor póliza', stroke: 'var(--bs-primary)' },
  { dataKey: 'accumulated_prime', name: 'Prima acumulada', stroke: 'var(--bs-gray-600)' },
  {
    dataKey: 'death_compensation',
    name: 'Indemn. fallecimiento',
    stroke: 'var(--bs-success)',
    strokeDasharray: '5 4',
  },
  { dataKey: 'agreed_deposits', name: 'Aportes acordados', stroke: 'var(--bs-info)' },
  {
    dataKey: 'fiscal_contributions',
    name: 'Aporte fiscal',
    stroke: 'var(--bs-warning)',
    strokeDasharray: '3 3',
  },
];

const FISCAL_COLUMN: ProjectionColumn<APVSelectPolicyRow> = {
  id: 'fiscal_contributions',
  label: 'Aporte fiscal',
  isMoney: true,
};

const APV_SELECT_COLUMNS = buildProjectionColumns<APVSelectPolicyRow>([FISCAL_COLUMN]);

const MONTHLY_CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor póliza', stroke: 'var(--bs-primary)' },
];

const APV_SELECT_MONTHLY_COLUMNS = buildMonthlyProjectionColumns<APVSelectMonthlyRow>([
  { id: 'fiscal_contributions', label: 'Aporte fiscal', isMoney: true },
]);

interface APVSelectResultViewProps {
  request: unknown;
  response?: APVSelectResponse;
  error?: unknown;
}

export function APVSelectResultView({
  request,
  response,
  error,
}: APVSelectResultViewProps) {
  const [currency, setCurrency] = useState<Currency>('UF');
  const hasSuccess = !!response && !error;

  return (
    <div className="mt-4">
      {hasSuccess && response && (
        <>
          <CurrencyHeader
            ufValue={response.uf_value}
            currency={currency}
            onCurrencyChange={setCurrency}
          />

          <SimulationWarnings warnings={response.warnings} />

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Pensión total proyectada"
                value={formatMoney(response.total_monthly_projected_pension, currency, response.uf_value)}
                hint="AFP + APV + BICE"
                accent="text-primary"
              />
              <KpiCard
                label="Pensión solo AFP"
                value={formatMoney(response.monthly_pension_with_afp, currency, response.uf_value)}
              />
              <KpiCard
                label="Pensión AFP + traspaso APV"
                value={formatMoney(response.monthly_pension_afp_with_apv_transfer, currency, response.uf_value)}
              />
              <KpiCard
                label="Pensión AFP + traspaso + BICE"
                value={formatMoney(response.monthly_pension_afp_apv_transfer_and_bice, currency, response.uf_value)}
                accent="text-success"
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Ahorro total (AFP + APV)"
                value={formatMoney(response.total_savings, currency, response.uf_value)}
                accent="text-primary"
              />
              <KpiCard
                label="Ahorro AFP"
                value={formatMoney(response.total_afp_savings, currency, response.uf_value)}
              />
              <KpiCard
                label="Ahorro APV"
                value={formatMoney(response.total_apv_savings, currency, response.uf_value)}
                hint={response.total_apv_savings < 0 ? 'Póliza insuficiente' : undefined}
                accent={response.total_apv_savings < 0 ? 'text-danger' : 'text-success'}
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Prima acordada"
                value={formatMoney(response.premium.agreed, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima básica"
                value={formatMoney(response.premium.basic, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima excedente"
                value={formatMoney(response.premium.excess, currency, response.uf_value)}
                accent="text-danger"
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <EvolutionChart
              data={buildEvolutionSeries(
                response.projection_by_year,
                ['policy_value', 'accumulated_prime', 'death_compensation', 'agreed_deposits', 'fiscal_contributions'],
                currency,
                response.uf_value,
              )}
              series={CHART_SERIES}
              currency={currency}
            />
          </div>

          {response.projection_by_month && (
            <>
              <div className="mb-4">
                <EvolutionChart
                  title="Evolución mensual de la póliza"
                  data={buildEvolutionSeries(
                    response.projection_by_month,
                    ['policy_value'],
                    currency,
                    response.uf_value,
                    ['month'],
                  )}
                  series={MONTHLY_CHART_SERIES}
                  currency={currency}
                  xKey="month"
                  xLabel="Mes"
                />
              </div>

              <div className="mb-4">
                <ProjectionTable
                  rows={response.projection_by_month}
                  columns={APV_SELECT_MONTHLY_COLUMNS}
                  currency={currency}
                  ufValue={response.uf_value}
                  title="Detalle mensual"
                  rowKey="month"
                />
              </div>
            </>
          )}

          <div className="mb-4">
            <ProjectionTable
              rows={response.projection_by_year}
              columns={APV_SELECT_COLUMNS}
              currency={currency}
              ufValue={response.uf_value}
            />
          </div>
        </>
      )}

      <RequestResponseViewer request={request} response={response} error={error} />
    </div>
  );
}
